<template>
  <v-container fluid>
    <changelog-alert />

    <dashboard-period-filter :value="period" @input="onPeriodUpdate" />

    <section v-if="showKpis" class="mb-4">
      <dashboard-kpis
        :kpis="dashboardData?.kpis"
        :loading="loading"
        :permissions="dashboardPermissions"
      />
    </section>

    <section v-if="hasPermission(PERM_FINANCIAL)" class="mb-4">
      <dashboard-rankings
        :top-events="dashboardData?.topEvents"
        :revenue-by-method="dashboardData?.revenueByMethod"
        :loading="loading"
      />
    </section>

    <section v-if="hasPermission(PERM_REFUNDS)" class="mb-4">
      <dashboard-refunds
        :refunds="dashboardData?.refunds"
        :loading="loading"
      />
    </section>

    <section v-if="hasPermission(PERM_EVENTS)">
      <h6 class="text-subtitle-1 font-weight-bold mb-2">
        Linha do Tempo Operacional
      </h6>
      <dashboard-timeline
        :timeline="dashboardData?.timeline"
        :loading="loading"
      />
    </section>
  </v-container>
</template>

<script>
import { mapGetters } from "vuex";
import moment from "moment";
import DASHBOARD from "@/services/admin/dashboard";
import ChangelogAlert from "@/components/global/ChangelogAlert.vue";
import DashboardPeriodFilter from "@/components/admin/dashboard/DashboardPeriodFilter.vue";
import DashboardKpis from "@/components/admin/dashboard/DashboardKpis.vue";
import DashboardRankings from "@/components/admin/dashboard/DashboardRankings.vue";
import DashboardRefunds from "@/components/admin/dashboard/DashboardRefunds.vue";
import DashboardTimeline from "@/components/admin/dashboard/DashboardTimeline.vue";

const PERM_FINANCIAL = 16;
const PERM_REFUNDS = 64;
const PERM_EVENTS = 4096;
const REFRESH_INTERVAL_MS = 10 * 60 * 1000;

export default {
  components: {
    ChangelogAlert,
    DashboardPeriodFilter,
    DashboardKpis,
    DashboardRankings,
    DashboardRefunds,
    DashboardTimeline,
  },
  data: () => ({
    PERM_FINANCIAL,
    PERM_REFUNDS,
    PERM_EVENTS,
    loading: false,
    fetching: false,
    dashboardData: null,
    period: {
      start: moment().subtract(6, "days").startOf("day").toISOString(),
      end: moment().endOf("day").toISOString(),
    },
    refreshTimer: null,
  }),
  computed: {
    ...mapGetters("auth", ["hasPermission"]),
    ...mapGetters("company", ["selectedCompany"]),
    dashboardPermissions() {
      return {
        financial: this.hasPermission(PERM_FINANCIAL),
        events: this.hasPermission(PERM_EVENTS),
      };
    },
    showKpis() {
      const p = this.dashboardPermissions;
      return p.financial || p.events;
    },
  },
  watch: {
    "selectedCompany.id"() {
      this.dashboardData = null;
      this.fetchDashboard({ showLoading: true });
    },
  },
  mounted() {
    this.fetchDashboard();
    this.startAutoRefresh();
  },
  beforeDestroy() {
    this.stopAutoRefresh();
  },
  methods: {
    onPeriodUpdate(period) {
      this.period = period;
      this.fetchDashboard({ showLoading: true });
    },
    async fetchDashboard({ showLoading = !this.dashboardData } = {}) {
      if (!this.period?.start || !this.period?.end) return;
      if (this.fetching) return;

      try {
        this.fetching = true;
        if (showLoading) this.loading = true;

        this.dashboardData = await DASHBOARD.getDashboard({
          start: this.period.start,
          end: this.period.end,
        });
      } catch (error) {
        console.error(error);
      } finally {
        this.fetching = false;
        if (showLoading) this.loading = false;
      }
    },
    startAutoRefresh() {
      this.stopAutoRefresh();
      this.refreshTimer = setInterval(() => {
        this.fetchDashboard({ showLoading: false });
      }, REFRESH_INTERVAL_MS);
    },
    stopAutoRefresh() {
      if (this.refreshTimer) {
        clearInterval(this.refreshTimer);
        this.refreshTimer = null;
      }
    },
  },
};
</script>

<style></style>
